import { useCurrentContext } from "@/hooks/use-current-context"
import { cn } from "@/lib/utils"
import type { CurrentBrand } from "@/types"

interface CurrentBrandDisplayProps {
  className?: string
  /**
   * Truncate the visible name at this many characters. Spec defaults to 20
   * on desktop / 14 on mobile. Pass undefined to disable truncation.
   */
  truncateAt?: number
  /** Optional override (defaults to `useCurrentContext().currentBrand`). */
  brand?: CurrentBrand | null
}

/**
 * CurrentBrandDisplay — read-only brand chip (logo + name) for shells where
 * the brand is fixed and no switcher applies (single-brand agency / brand user).
 *
 * Renders nothing when there is no current brand — the parent shell decides
 * whether to show a placeholder. For agency users with 0 active brand
 * assignments, the OKS-117 dispatcher routes them server-side to
 * `/agency/brands` (existing OKS-13 F6 empty-state CTA), so this component
 * is not in the visual path for that case.
 */
export function CurrentBrandDisplay({
  className,
  truncateAt = 20,
  brand: brandProp,
}: CurrentBrandDisplayProps) {
  const ctx = useCurrentContext()
  const brand = brandProp ?? ctx.currentBrand
  if (!brand) return null

  const displayName =
    typeof truncateAt === "number" && brand.name.length > truncateAt
      ? `${brand.name.slice(0, truncateAt - 1)}…`
      : brand.name

  return (
    <div
      className={cn(
        "flex min-w-0 items-center gap-2 text-sm font-medium",
        className,
      )}
      data-testid="current-brand-display"
    >
      {brand.logo_url ? (
        <img
          src={brand.logo_url}
          alt=""
          className="size-6 shrink-0 rounded-full object-cover"
        />
      ) : (
        <div
          aria-hidden="true"
          className="bg-muted text-muted-foreground flex size-6 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold"
        >
          {brand.name.slice(0, 2).toUpperCase()}
        </div>
      )}
      <span className="truncate" title={brand.name}>
        {displayName}
      </span>
    </div>
  )
}
